import type {
  AiAutomationRunAnsweredQuestion,
  AiAutomationRunToolCall,
  ConversationId,
} from "@ctrl/controller-rpc";

/**
 * A tool call in a normalized, discriminated shape.
 *
 * The generated `AiAutomationRunToolCall` union carries a "without kind"
 * representation that never occurs at runtime (the controller always tags tool
 * calls with a `kind`), and that representation defeats discriminant narrowing.
 * This module recovers a clean discriminated union at the RPC boundary so the
 * rest of the client can pattern-match without unsafe assertions.
 */
export type NormalizedToolCall =
  | {
      kind: "ask_user";
      answeredQuestions: AiAutomationRunAnsweredQuestion[];
      conversation: ConversationId | null;
    }
  | { kind: "call"; name: string; arguments: unknown; result: string };

export function normalizeToolCall(
  call: AiAutomationRunToolCall
): NormalizedToolCall {
  if ("kind" in call && call.kind === "ask_user") {
    return {
      kind: "ask_user",
      answeredQuestions: call.response?.answered_questions ?? [],
      conversation: call.conversation ?? null,
    };
  }
  if ("name" in call) {
    return {
      kind: "call",
      name: call.name,
      arguments: call.arguments,
      result: call.result,
    };
  }
  // Unreachable at runtime: the generator's untagged ask_user form.
  return {
    kind: "ask_user",
    answeredQuestions: call.response.answered_questions,
    conversation: call.conversation ?? null,
  };
}
